// frontend/src/components/NvrVideoModal.js
import React, { useState, useEffect, useCallback, useRef } from 'react'; // Добавлен useRef
import axios from 'axios';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Box,
    CircularProgress,
    Typography,
    Alert,
    AlertTitle,
    Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const NvrVideoModal = ({ open, onClose, device }) => {
    const [loadingStream, setLoadingStream] = useState(false);
    const [streamUrl, setStreamUrl] = useState('');
    const [currentStreamName, setCurrentStreamName] = useState('');
    const [error, setError] = useState(null);
    const [videoElement, setVideoElement] = useState(null);

    // --- НОВОЕ: Состояния для перетаскивания ---
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [dialogPosition, setDialogPosition] = useState({ x: 0, y: 0 });
    const dialogRef = useRef(); // Ссылка на элемент диалога

    // --- НОВОЕ: Обработчики событий мыши для перетаскивания ---
    const handleMouseDown = (e) => {
        // Проверяем, что клик был на заголовке, а не на кнопке закрытия
        if (e.target.closest('button')) return;

        setIsDragging(true);
        const dialog = dialogRef.current?.closest('.MuiDialog-paper');
        if (dialog) {
            const rect = dialog.getBoundingClientRect();
            setDragOffset({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            });
        }
        e.preventDefault();
    };

    const handleMouseMove = useCallback((e) => {
        if (isDragging) {
            setDialogPosition({
                x: e.clientX - dragOffset.x,
                y: e.clientY - dragOffset.y
            });
        }
    }, [isDragging, dragOffset]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Добавляем глобальные слушатели событий мыши
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        } else {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);
    // --- КОНЕЦ НОВОГО ---

    const isHlsSupported = () => {
        if (typeof Hls !== 'undefined' && Hls.isSupported()) {
            return true;
        }
        if (window.MediaSource && window.MediaSource.isTypeSupported('application/vnd.apple.mpegurl')) {
            return true;
        }
        return false;
    };

    const startStream = useCallback(async (deviceId) => {
        setLoadingStream(true);
        setStreamUrl('');
        setCurrentStreamName('');
        setError(null);

        try {
            const response = await axios.post(`/api/start_nvr_stream/${deviceId}`);
            const streamData = response.data;

            setStreamUrl(streamData.stream_url);
            setCurrentStreamName(streamData.stream_name);
        } catch (err) {
            console.error('Ошибка запуска потока:', err);
            let errorMsg = 'Неизвестная ошибка';
            if (err.response) {
                if (err.response.status === 404) {
                    errorMsg = 'Устройство не найдено.';
                } else if (err.response.status === 400) {
                    errorMsg = 'Недостаточно данных NVR для устройства.';
                } else if (err.response.status >= 500) {
                    errorMsg = `Ошибка сервера: ${err.response.data?.detail || 'Внутренняя ошибка'}`;
                } else {
                    errorMsg = `Ошибка ${err.response.status}: ${err.response.data?.detail || 'Неизвестная ошибка'}`;
                }
            } else if (err.request) {
                errorMsg = 'Сервер не отвечает.';
            } else {
                errorMsg = err.message;
            }
            setError(errorMsg);
        } finally {
            setLoadingStream(false);
        }
    }, []);

    const stopStream = useCallback(async (streamName) => {
        if (streamName) {
            try {
                await axios.delete(`/api/stop_nvr_stream/${streamName}`);
                console.log(`Поток ${streamName} остановлен на сервере.`);
            } catch (err) {
                console.error('Ошибка остановки потока на сервере:', err);
            }
        }
    }, []);

    useEffect(() => {
        if (open && device?.id) {
            startStream(device.id);
        }
    }, [open, device?.id, startStream]);

    useEffect(() => {
        return () => {
            if (currentStreamName) {
                stopStream(currentStreamName);
            }
        };
    }, [currentStreamName, stopStream]);

    useEffect(() => {
        if (!open || !streamUrl || !videoElement) return;

        const video = videoElement;
        let hlsInstance = null;

        if (typeof Hls !== 'undefined' && Hls.isSupported()) {
            hlsInstance = new Hls();
            hlsInstance.on(Hls.Events.MANIFEST_PARSED, function () {
                console.log("MANIFEST_PARSED");
                video.play().catch(e => console.error("Ошибка автовоспроизведения:", e));
            });

            hlsInstance.on(Hls.Events.ERROR, function (event, data) {
                console.error("HLS ERROR:", event, data);
                if (data.fatal) {
                    if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
                        console.error("NETWORK ERROR");
                    } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
                        console.error("MEDIA ERROR");
                    }
                    hlsInstance.destroy();
                    setError("Ошибка воспроизведения видеопотока.");
                }
            });

            try {
                hlsInstance.loadSource(streamUrl);
                hlsInstance.attachMedia(video);
                hlsInstance.startLoad();
            } catch (e) {
                console.error("Ошибка при инициализации hls.js:", e);
                if (isHlsSupported() && window.MediaSource && window.MediaSource.isTypeSupported('application/vnd.apple.mpegurl')) {
                    video.src = streamUrl;
                    video.load();
                } else {
                    setError("Ваш браузер не поддерживает воспроизведение видеопотока.");
                }
            }
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = streamUrl;
            video.addEventListener('loadedmetadata', function () {
                video.play().catch(e => console.error("Ошибка автовоспроизведения:", e));
            });
        } else {
            setError("Ваш браузер не поддерживает воспроизведение этого видеопотока.");
        }

        return () => {
            if (hlsInstance) {
                hlsInstance.destroy();
            }
        };
    }, [open, streamUrl, videoElement]);

    const handleClose = () => {
        if (currentStreamName) {
            stopStream(currentStreamName);
        }
        setStreamUrl('');
        setCurrentStreamName('');
        setError(null);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            aria-labelledby="video-dialog-title"
            aria-describedby="video-dialog-description"
            // --- НОВОЕ: Стили для позиционирования и перетаскивания ---
            style={{
                position: 'absolute',
                margin: 0,
                top: dialogPosition.y || '50%',
                left: dialogPosition.x || '50%',
                transform: dialogPosition.x || dialogPosition.y ? 'none' : 'translate(-50%, -50%)',
            }}
            PaperProps={{
                sx: {
                    minHeight: '50vh',
                    margin: 0,
                },
                ref: dialogRef // Привязываем ссылку к элементу Paper диалога
            }}
            // --- КОНЕЦ НОВОГО ---
        >
            {/* --- НОВОЕ: Заголовок с поддержкой перетаскивания --- */}
            <DialogTitle
                id="video-dialog-title"
                onMouseDown={handleMouseDown}
                sx={{
                    cursor: 'move',
                    userSelect: 'none',
                    position: 'relative',
                    pr: 8,
                }}
            >
                Видеопоток с камеры
                <Button
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: 'grey.500',
                    }}
                >
                    <CloseIcon />
                </Button>
            </DialogTitle>
            {/* --- КОНЕЦ НОВОГО --- */}
            <DialogContent
                dividers
                sx={{
                    p: 0,
                    bgcolor: 'black',
                    height: 'auto',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {loadingStream ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>Запуск видеопотока...</Typography>
                    </Box>
                ) : error ? (
                    <Box sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Alert severity="error" sx={{ width: '100%' }}>
                            <AlertTitle>Ошибка</AlertTitle>
                            {error}
                        </Alert>
                    </Box>
                ) : streamUrl ? (
                    // --- ИСПРАВЛЕНО: Контейнер с фиксированным соотношением сторон 16:9 ---
                    <Box
                        sx={{
                            position: 'relative',
                            width: '100%',
                            '&::before': {
                                content: '""',
                                display: 'block',
                                paddingTop: '56.25%', // 16:9 aspect ratio
                            },
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                bgcolor: 'black',
                            }}
                        >
                            <video
                                ref={(el) => setVideoElement(el)}
                                controls
                                autoPlay
                                playsInline
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover', // Исправлена опечатка: было 'сover'
                                    display: 'block',
                                }}
                            />
                        </Box>
                    </Box>
                    // --- КОНЕЦ ИСПРАВЛЕНИЯ ---
                ) : (
                    <Box sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Alert severity="info" sx={{ width: '100%' }}>
                            <AlertTitle>Информация</AlertTitle>
                            URL потока не предоставлен. Попробуйте закрыть и открыть снова.
                        </Alert>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default NvrVideoModal;